import { AppLayout } from "@/components/AppLayout";
import { Lightbulb, Sparkles, TrendingUp, Heart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { usePublicGallery, categoryDisplayNames } from "@/hooks/usePublicGallery";
import { useGalleryLikes } from "@/hooks/useGalleryLikes";
import { cn } from "@/lib/utils";

// Fallback data when database is empty
const fallbackCategories = [
  {
    title: "Portraits réalistes",
    category: "portrait",
    images: [
      { id: "fp1", url: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop", prompt: "Portrait photoréaliste d'une femme élégante", likes_count: 0 },
      { id: "fp2", url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop", prompt: "Portrait cinématique avec éclairage dramatique", likes_count: 0 },
      { id: "fp3", url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop", prompt: "Portrait artistique style magazine mode", likes_count: 0 },
    ],
  },
  {
    title: "Art digital & Fantasy",
    category: "fantasy",
    images: [
      { id: "ff1", url: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&h=400&fit=crop", prompt: "Paysage fantastique avec château flottant", likes_count: 0 },
      { id: "ff2", url: "https://images.unsplash.com/photo-1541701494587-cb58502866ab?w=400&h=400&fit=crop", prompt: "Art digital abstrait avec néons", likes_count: 0 },
      { id: "ff3", url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&h=400&fit=crop", prompt: "Scène épique de bataille fantasy", likes_count: 0 },
    ],
  },
  {
    title: "Paysages & Nature",
    category: "landscape",
    images: [
      { id: "fl1", url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=400&fit=crop", prompt: "Coucher de soleil sur montagne enneigée", likes_count: 0 },
      { id: "fl2", url: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&h=400&fit=crop", prompt: "Forêt enchantée avec lumière mystique", likes_count: 0 },
      { id: "fl3", url: "https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=400&h=400&fit=crop", prompt: "Cascade tropicale paradisiaque", likes_count: 0 },
    ],
  },
];

const Inspiration = () => {
  const navigate = useNavigate();
  const { data: dbImages, isLoading } = usePublicGallery(undefined, 100);

  // Get all image IDs for likes hook
  const allImageIds = dbImages ? dbImages.map(img => img.id) : [];
  const { toggleLike, isLiked, isLoading: likeLoading } = useGalleryLikes(allImageIds);

  const handleUsePrompt = (prompt: string) => {
    navigate("/create", { state: { prompt } });
  };

  const handleLike = (e: React.MouseEvent, imageId: string) => {
    e.stopPropagation();
    if (!imageId.startsWith("f")) {
      toggleLike(imageId);
    }
  };

  // Group database images by category
  const getGroupedImages = () => {
    if (!dbImages || dbImages.length === 0) {
      return fallbackCategories;
    }

    const grouped: Record<string, { id: string; url: string; prompt: string; likes_count: number }[]> = {};
    
    dbImages.forEach((img) => {
      if (!grouped[img.category]) {
        grouped[img.category] = [];
      }
      if (grouped[img.category].length < 6) {
        grouped[img.category].push({
          id: img.id,
          url: img.image_url,
          prompt: img.prompt,
          likes_count: img.likes_count,
        });
      }
    });

    return Object.entries(grouped).map(([category, images]) => ({
      title: categoryDisplayNames[category] || category,
      category,
      images,
    }));
  };

  const categories = getGroupedImages();

  // Extract trending prompts from database (sorted by likes)
  const trendingPrompts = dbImages && dbImages.length > 0
    ? [...dbImages]
        .sort((a, b) => b.likes_count - a.likes_count)
        .slice(0, 6)
        .map(img => img.prompt.slice(0, 50) + (img.prompt.length > 50 ? "..." : ""))
    : [
        "Portrait studio professionnel",
        "Paysage cyberpunk néon",
        "Chat astronaute kawaii",
        "Logo minimaliste moderne",
        "Illustration livre pour enfants",
        "Photo produit e-commerce",
      ];

  return (
    <AppLayout title="Inspiration">
      <div className="p-6 space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
            <Lightbulb className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-primary">Galerie d'inspiration</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            Trouvez l'inspiration pour vos créations
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explorez les créations de notre communauté et votez pour vos préférées !
          </p>
        </div>

        {/* Trending Prompts */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-highlight" />
            <h2 className="text-lg font-semibold text-foreground">Prompts tendances</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {trendingPrompts.map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => handleUsePrompt(prompt)}
                className="px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-xl text-sm text-foreground transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Categories */}
        {!isLoading && categories.map((category) => (
          <div key={category.category} className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">{category.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {category.images.map((image, index) => (
                <div
                  key={image.id}
                  className="group relative bg-card border border-border rounded-2xl overflow-hidden"
                >
                  <img
                    src={image.url}
                    alt={image.prompt}
                    className="w-full aspect-square object-cover"
                    loading="lazy"
                  />
                  {/* Like button and count */}
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    {image.likes_count > 0 && (
                      <span className="px-2 py-1 rounded-full bg-background/70 backdrop-blur-sm text-xs font-medium text-foreground flex items-center gap-1">
                        <Heart className="w-3 h-3 fill-primary text-primary" />
                        {image.likes_count}
                      </span>
                    )}
                    <button
                      onClick={(e) => handleLike(e, image.id)}
                      disabled={likeLoading || image.id.startsWith("f")}
                      className={cn(
                        "p-2 rounded-full backdrop-blur-sm transition-all",
                        isLiked(image.id)
                          ? "bg-primary/90 text-white"
                          : "bg-background/70 text-foreground hover:bg-background/90"
                      )}
                    >
                      <Heart className={cn("w-4 h-4", isLiked(image.id) && "fill-current")} />
                    </button>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-sm text-foreground mb-3 line-clamp-2">
                        "{image.prompt}"
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUsePrompt(image.prompt)}
                          className="flex-1 bg-gradient-floop hover:opacity-90 text-white rounded-xl"
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          Utiliser
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {!isLoading && categories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Aucune image d'inspiration disponible pour le moment.</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default Inspiration;
